// Real-time change notifications over WebSocket. The backend can't APNs a
// browser, so the SPA holds a socket: on any message ("your data changed") it
// re-pulls. Correctness comes from the pull — the socket is only a nudge — so a
// dropped message or reconnect gap self-heals on the next refresh.
//
// Auth note: API Gateway only checks the JWT at the $connect handshake, and a
// rejected handshake is indistinguishable from a network drop in the browser
// (both surface as close code 1006). So we can't refresh *reactively* here — we
// refresh PROACTIVELY, resolving a guaranteed-fresh token before every connect.
// This also survives API Gateway's 2h max connection duration: the forced
// reconnect lands after the 1h access token has expired, and would otherwise
// loop forever on Deny.

const DEFAULT_WS_URL = "wss://ws.assistant8.com";
const MAX_BACKOFF_MS = 30_000;
const BASE_BACKOFF_MS = 1_000;

export interface RealtimeOptions {
  /** Resolves a valid access token, or null when signed out (then we don't connect). */
  getToken: () => Promise<string | null>;
  /**
   * Called on every change notification with the latest feed `seq` when the
   * message carries one — pull only if behind it.
   */
  onChange: (latestSeq?: number) => void;
  /** Override the WS endpoint (defaults to VITE_WS_URL or the prod domain). */
  url?: string;
}

/** A self-reconnecting WebSocket bound to the signed-in user via the JWT. */
export class RealtimeClient {
  private socket: WebSocket | null = null;
  private stopped = false;
  private attempt = 0;
  private readonly url: string;

  constructor(private readonly opts: RealtimeOptions) {
    this.url = opts.url ?? import.meta.env.VITE_WS_URL ?? DEFAULT_WS_URL;
  }

  /** Open the connection (no-op if not signed in). */
  start(): void {
    this.stopped = false;
    void this.connect();
  }

  /** Close for good — call on sign-out / unmount. */
  stop(): void {
    this.stopped = true;
    this.socket?.close();
    this.socket = null;
  }

  private async connect(): Promise<void> {
    if (this.stopped || this.socket) return;
    // Proactively refresh if needed BEFORE handshaking — a stale token here just
    // gets silently Denied with no way to tell why. A rejected refresh (offline,
    // 5xx cold start) must NOT kill the loop: catch it and schedule a retry, so
    // the socket comes back on its own once connectivity/token recover.
    let token: string | null;
    try {
      token = await this.opts.getToken();
    } catch {
      if (!this.stopped) this.scheduleReconnect();
      return;
    }
    // A concurrent stop()/connect() may have raced the await; re-check.
    if (!token || this.stopped || this.socket) return;

    // Browsers can't set headers on the handshake, so the JWT rides the query
    // string; the $connect Lambda authorizer verifies it.
    const socket = new WebSocket(`${this.url}/?token=${encodeURIComponent(token)}`);
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
    };
    socket.onmessage = (event) => {
      this.opts.onChange(parseSeq(event.data));
    };
    socket.onerror = () => socket.close();
    socket.onclose = () => {
      this.socket = null;
      if (!this.stopped) this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    const delay = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** this.attempt);
    this.attempt += 1;
    setTimeout(() => void this.connect(), delay);
  }
}

/** The nudge payload is {"type":"dataChanged","seq":N}; seq is optional. */
function parseSeq(data: unknown): number | undefined {
  if (typeof data !== "string") return undefined;
  try {
    const seq: unknown = (JSON.parse(data) as { seq?: unknown }).seq;
    return typeof seq === "number" ? seq : undefined;
  } catch {
    return undefined;
  }
}
